import { Component, useContext } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { ChartBar } from 'components/ChartBar';
import cx from 'classnames';
import { TenantUsageEntries } from './TenantUsageEntries';
import { Loader } from 'components/Loader';
import { NotificationContext, NotificationContextState } from 'context/NotificationContext';
import { Tenant } from 'logic/apiCalls';
import * as apiCalls from 'logic/apiCalls';
import { RangeOption, rangeOptions } from 'logic/billingMetricLogic';
import { BillingMetricInfo, ChartData, ChartInfo, createChartSubText } from 'logic/billingMetricLogic';
import * as misc from 'utils/misc';

import css from './TabTenantsUsage.module.scss';

import log from 'loglevel';


type TenantsUsagePageState = {
    isLoading: boolean,
    isUpdating: boolean,
    showApiError: boolean,
    tenants: Tenant[],
    selectedGroupOption: RangeOption,
    selectedRange: string,
    resolution: string,
    showStacked: boolean,
    selectedTenant: Tenant | null
}

type TenantsUsageInternalProps = {
    notificationContext: NotificationContextState
}

export class TenantUsageInternal extends Component<TenantsUsageInternalProps, TenantsUsagePageState> {
    state: TenantsUsagePageState = {
        isLoading: true,
        isUpdating: false,
        showApiError: false,
        tenants: [],
        selectedGroupOption: rangeOptions[0],
        selectedRange: rangeOptions[0].value,
        resolution: rangeOptions[0].resolution,
        showStacked: rangeOptions[0].stacked,
        selectedTenant: null

    }
    metricInfos: any[] = [];
    metricData: any[] = [];
    metricStartDate?: string | null = null;
    metricEndDate?: string | null = null;

    async componentDidMount() {
        await this.loadPageData();
    }
    async updateMetrics(useSelectedTenant: Tenant | null = null) {

        let selTenant = useSelectedTenant;
        if (!useSelectedTenant) {
            selTenant = this.state.selectedTenant;
        }
        if (!selTenant) return;
        try {
            const metricsInfoPromise = apiCalls.getMetricInfos();
            const metricsPromise = apiCalls.getSystemTenantMetrics(selTenant.id, this.state.selectedRange);

            const [metricInfos, TenantMetrics] = await Promise.all([metricsInfoPromise, metricsPromise]);

            this.metricInfos = metricInfos;
            const billingInfos: BillingMetricInfo[] = [];
            for (const metricInfo of metricInfos) {
                const data: ChartData[] = [];
                for (const met of TenantMetrics.metrics) {
                    if (met.metric === metricInfo.metric) {
                        let i = 0;
                        for (const val of met.metricValues) {
                            data.push({
                                date: met.labels[i],
                                value: misc.round(val, 2)
                            });
                            i++;
                        }
                    }
                }

                const chartInfo: ChartInfo = {
                    data: data,
                    definitions: [{
                        displayName: metricInfo.unitOfMeasure,
                        valueName: "value"
                    }]
                };
                billingInfos.push({
                    displayName: metricInfo.displayName,
                    metric: chartInfo
                });
            }
            this.metricData = billingInfos;

            this.setState({
                isLoading: false,
                showApiError: false
            });
        }
        catch (error) {
            log.error(`Error during retrieval of the billing information:${error}`);

            this.setState({
                isLoading: false,
                showApiError: true
            });
        }
    }

    handleLineChartButtonClicked = async (tenantToUpdate: Tenant) => {

        this.setState({
            isUpdating: true,
            showApiError: false,
            selectedTenant: tenantToUpdate
        });
        await this.updateMetrics(tenantToUpdate);
        this.setState({
            isUpdating: false,
            showApiError: false
        });

    }
    handleRangeChange = async (newValue: RangeOption | null) => {
        if (!newValue) return;

        try {
            this.setState({
                selectedGroupOption: newValue,
                selectedRange: newValue.value,
                resolution: newValue.resolution,
                showStacked: newValue.stacked,
                isUpdating: true,
                showApiError: false
            }, async () => await this.updateMetrics(null));


            this.setState({
                isUpdating: false,
                showApiError: false
            });
        }
        catch (error) {
            log.error(`Error during updating of the usage range:${error}`);

            this.setState({
                isUpdating: false,
                showApiError: true
            });
        }
    }
    createChartSubTextJsx = (chartData: any[], unitDisplayName: string): JSX.Element => {

        let [text, val] = createChartSubText(chartData, unitDisplayName);

        return <>{text} in this period:<b>{val}</b></>
    }

    loadPageData = async () => {
        try {
            const tenants = await apiCalls.getTenants();

            this.setState({
                tenants: tenants,
                isLoading: false
            });
        }
        catch (error) {
            log.error(`Something went wrong: ${error}`);
        }
    }

    renderTenants = () => {
        return <div className="my-3">
            <TenantUsageEntries tenants={this.state.tenants} handleLineChart={this.handleLineChartButtonClicked} />
        </div>
    }

    renderChartTitle = () => {
        if (this.state.selectedTenant) {
            return (
                <>
                    <br />
                    <h2>Usage for tenant: {this.state.selectedTenant.label}</h2>
                </>
            );
        }
        return <br />
    }

    render() {
        return (
            <Loader isLoading={this.state.isLoading} showApiError={this.state.showApiError}>
                <>
                    <h1>Tenants usage</h1>
                    {this.renderTenants()}
                    {this.renderChartTitle()}
                    <div className={css.rangeSelect}>
                        <Select
                            className="my-3"
                            inputValue=''
                            value={this.state.selectedGroupOption}
                            options={rangeOptions}
                            onChange={this.handleRangeChange}
                            placeholder='Select range...' />
                    </div>
                    {this.metricData.map(entry =>
                        <Row key={entry.displayName}>
                            <Col lg>
                                <Card className={cx("my-3", css.card)}>
                                    <Card.Header><h5>{entry.displayName}</h5>from {this.metricStartDate?.toString()} until {this.metricEndDate?.toString()}</Card.Header>
                                    <Card.Body>
                                        <ChartBar data={entry.metric.data} keyName="date" bars={entry.metric.definitions} isLoading={this.state.isUpdating} />
                                        {this.createChartSubTextJsx(entry.metric.data, entry.metric.definitions[0].displayName)}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            </Loader>

        );
    }
}

// Inject the required contexts.
export const TabTenantsUsageView = (props: any) => {
    const notificationContext = useContext(NotificationContext);

    return (
        <TenantUsageInternal {...props} notificationContext={notificationContext} />
    )
}